import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import { Grid, Spin, Tooltip, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";

import { loadAppointments, setAppointmentDone } from "../../slice";
import { selectAppointmentsList } from "../../selector";
import {
  DEFAULT_PAGE_SIZE,
  pageSizeOptions,
} from "../../../../../utils/pagination";
import { useTableBodyHeight } from "../../../../../utils/useTableBodyHeight";
import { API_ENDPOINT } from "../../../../../utils/constants";
import request, { type RequestError } from "../../../../../utils/request";
import type { Appointment, AppointmentFilter, DateRange } from "../../types";
import AppointmentDetailModal from "../AppointmentDetailModal";
import SetStatusCell from "../../../../components/SetStatusCell";
import {
  AppointmentsCard,
  DateText,
  DateTimeCell,
  DentistText,
  DesktopTableWrap,
  EntriesFooter,
  FilterButton,
  FilterScroll,
  MobileCard,
  MobileCardTop,
  MobileList,
  MobileStatusRow,
  MobileViewButton,
  PatientCell,
  PatientName,
  PatientPhone,
  SearchInput,
  ServiceTag,
  ShowEntriesSelect,
  ShowEntriesWrap,
  StatusTag,
  StyledTable,
  TableControls,
  TableHeader,
  TablePagination,
  TableSubtitle,
  TableTitle,
  TableTitleWrap,
  TimeText,
  ViewButton,
} from "../../style";

const { Text } = Typography;
const { useBreakpoint } = Grid;

const SERVICE_COLORS: Record<string, { color: string; bg: string }> = {
  "Routine Cleaning": { color: "#0e7a6e", bg: "#d1faf6" },
  "Teeth Whitening": { color: "#be185d", bg: "#fce7f3" },
  "Root Canal": { color: "#b45309", bg: "#fef3c7" },
  "Dental Filling": { color: "#6d28d9", bg: "#ede9fe" },
  "Tooth Extraction": { color: "#c2410c", bg: "#ffedd5" },
};

const COLORS_FALLBACK = { color: "#4F78A1", bg: "#e8f0fb" };

interface FilterDef {
  key: AppointmentFilter;
  label: string;
}

const filterDefs: FilterDef[] = [
  { key: "all", label: "All Appointments" },
  { key: "scheduled", label: "Scheduled Appointments" },
  { key: "unsuccessful", label: "Unsuccessful Appointments" },
  { key: "cancelled", label: "Cancelled Appointments" },
];

interface AppointmentsTableProps {
  dateRange: DateRange;
}

export default function AppointmentsTable({
  dateRange,
}: AppointmentsTableProps) {
  const dispatch = useDispatch();
  const { data, loading } = useSelector(selectAppointmentsList);

  const [filter, setFilter] = useState<AppointmentFilter>("all");
  const [search, setSearch] = useState("");
  const [manualPageSize, setManualPageSize] = useState<number | null>(null);
  const [mobilePage, setMobilePage] = useState(1);
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [updatingKeys, setUpdatingKeys] = useState<Set<string>>(new Set());

  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  async function handleSetDone(row: Appointment, done: boolean) {
    if (updatingKeys.has(row.key)) return;
    setUpdatingKeys((prev) => new Set(prev).add(row.key));
    const endpoint = done
      ? "mark-appointment-call-done"
      : "mark-appointment-call-undone";
    try {
      const res = await request<{ is_done: boolean }>(
        `${API_ENDPOINT}${endpoint}?call_log_id=${encodeURIComponent(
          row.appointmentId,
        )}`,
        { method: "PATCH" },
      );
      dispatch(setAppointmentDone({ key: row.key, isDone: res.is_done }));
    } catch (e) {
      // 409 means it was already in the requested state — reflect that.
      if ((e as RequestError)?.status === 409) {
        dispatch(setAppointmentDone({ key: row.key, isDone: done }));
      } else {
        message.error("Could not update status. Please try again.");
      }
    } finally {
      setUpdatingKeys((prev) => {
        const next = new Set(prev);
        next.delete(row.key);
        return next;
      });
    }
  }

  // Manual selection from the "Show entries" control; defaults to 5 everywhere.
  const desktopPageSize = manualPageSize ?? DEFAULT_PAGE_SIZE;
  const mobilePageSize = manualPageSize ?? DEFAULT_PAGE_SIZE;
  const shownPageSize = isMobile ? mobilePageSize : desktopPageSize;

  const [rangeStart, rangeEnd] = dateRange ?? [null, null];

  useEffect(() => {
    dispatch(
      loadAppointments({
        startDate: rangeStart ? rangeStart.format("YYYY-MM-DD") : undefined,
        endDate: rangeEnd ? rangeEnd.format("YYYY-MM-DD") : undefined,
      }),
    );
  }, [dispatch, rangeStart, rangeEnd]);

  const filtered = useMemo(() => {
    const records = data ?? [];
    return records.filter((a) => {
      const statusLower = a.status.toLowerCase();
      if (filter === "scheduled" && statusLower !== "scheduled") return false;
      if (filter === "unsuccessful" && statusLower !== "unsuccessful")
        return false;
      if (filter === "cancelled" && statusLower !== "cancelled") return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        a.patientName.toLowerCase().includes(q) ||
        a.service.toLowerCase().includes(q) ||
        a.dentist.toLowerCase().includes(q)
      );
    });
  }, [data, filter, search]);

  const pagedMobile = useMemo(() => {
    const start = (mobilePage - 1) * mobilePageSize;
    return filtered.slice(start, start + mobilePageSize);
  }, [filtered, mobilePage, mobilePageSize]);

  const [tableWrapRef, scrollY] = useTableBodyHeight(
    !isMobile,
    desktopPageSize,
    filtered.length,
  );

  const columns: ColumnsType<Appointment> = [
    {
      title: "Patient Name",
      key: "patient",
      width: 180,
      render: (_v, row) => (
        <PatientCell vertical>
          <PatientName>{row.patientName}</PatientName>
          <PatientPhone>{row.patientPhone}</PatientPhone>
        </PatientCell>
      ),
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      width: 160,
      align: "center",
      render: (service: string) => {
        const colors = SERVICE_COLORS[service] ?? COLORS_FALLBACK;
        return (
          <Tooltip title={service}>
            <ServiceTag $color={colors.color} $bg={colors.bg}>
              {service}
            </ServiceTag>
          </Tooltip>
        );
      },
    },
    {
      title: "Dentist",
      dataIndex: "dentist",
      key: "dentist",
      width: 160,
      render: (dentist: string) => <DentistText>{dentist}</DentistText>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (status: string) => (
        <StatusTag $status={status}>{status}</StatusTag>
      ),
    },
    {
      title: "Date & Time",
      key: "datetime",
      width: 130,
      render: (_v, row) => (
        <DateTimeCell vertical>
          <DateText>{row.date}</DateText>
          <TimeText>{row.time}</TimeText>
        </DateTimeCell>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      align: "center",
      render: (_v, row) => (
        <ViewButton onClick={() => setSelectedAppointment(row)}>View</ViewButton>
      ),
    },
    {
      title: "Set status",
      key: "setStatus",
      width: 150,
      align: "center",
      render: (_v, row) => (
        <SetStatusCell
          isDone={row.isDone}
          updating={updatingKeys.has(row.key)}
          onMarkDone={() => handleSetDone(row, true)}
          onReset={() => handleSetDone(row, false)}
        />
      ),
    },
  ];

  return (
    <>
      <AppointmentsCard>
        <TableHeader
          align="flex-start"
          justify="space-between"
          gap={14}
          wrap="wrap"
        >
          <TableTitleWrap vertical gap={2}>
            <TableTitle level={5}>Appointments</TableTitle>
            <TableSubtitle>
              Click &apos;View&apos; to see the full appointment details
            </TableSubtitle>
          </TableTitleWrap>

          <TableControls align="center" gap={10} wrap="wrap">
            <SearchInput
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FilterScroll align="center" gap={4}>
              {filterDefs.map((f) => (
                <FilterButton
                  key={f.key}
                  $active={filter === f.key}
                  onClick={() => {
                    setFilter(f.key);
                    setMobilePage(1);
                  }}
                >
                  {f.label}
                </FilterButton>
              ))}
            </FilterScroll>
            {isMobile && (
              <ShowEntriesWrap align="center" gap={8}>
                <Text>Show</Text>
                <ShowEntriesSelect
                  value={shownPageSize}
                  onChange={(v) => setManualPageSize(Number(v))}
                  options={pageSizeOptions(shownPageSize)}
                />
                <Text>entries</Text>
              </ShowEntriesWrap>
            )}
          </TableControls>
        </TableHeader>

        {isMobile ? (
          <Spin spinning={loading && !data}>
            <MobileList vertical>
              {pagedMobile.map((a) => {
                const colors = SERVICE_COLORS[a.service] ?? COLORS_FALLBACK;
                return (
                  <MobileCard key={a.key}>
                    <MobileCardTop
                      align="center"
                      justify="space-between"
                      gap={8}
                    >
                      <PatientName>{a.patientName}</PatientName>
                      <StatusTag $status={a.status}>{a.status}</StatusTag>
                    </MobileCardTop>
                    <PatientPhone>{a.patientPhone}</PatientPhone>
                    <ServiceTag $color={colors.color} $bg={colors.bg}>
                      {a.service}
                    </ServiceTag>
                    <DentistText>{a.dentist}</DentistText>
                    <DateTimeCell align="center" gap={6}>
                      <DateText>{a.date}</DateText>
                      <TimeText>· {a.time}</TimeText>
                    </DateTimeCell>
                    <MobileStatusRow align="center">
                      <SetStatusCell
                        isDone={a.isDone}
                        updating={updatingKeys.has(a.key)}
                        onMarkDone={() => handleSetDone(a, true)}
                        onReset={() => handleSetDone(a, false)}
                      />
                    </MobileStatusRow>
                    <MobileViewButton
                      onClick={() => setSelectedAppointment(a)}
                    >
                      View
                    </MobileViewButton>
                  </MobileCard>
                );
              })}
            </MobileList>
            <TablePagination
              current={mobilePage}
              pageSize={mobilePageSize}
              total={filtered.length}
              showSizeChanger={false}
              onChange={(page) => setMobilePage(page)}
              itemRender={(_page, type, originalElement) => {
                if (type === "prev") return <Text>Previous</Text>;
                if (type === "next") return <Text>Next</Text>;
                return originalElement;
              }}
            />
          </Spin>
        ) : (
          <DesktopTableWrap ref={tableWrapRef}>
            <Spin spinning={loading && !data}>
              <StyledTable
                columns={columns as ColumnsType<unknown>}
                dataSource={filtered}
                tableLayout="fixed"
                scroll={{ y: scrollY }}
                pagination={{
                  pageSize: desktopPageSize,
                  showSizeChanger: false,
                  position: ["bottomCenter"],
                  itemRender: (_page, type, originalElement) => {
                    if (type === "prev") return <Text>Previous</Text>;
                    if (type === "next") return <Text>Next</Text>;
                    return originalElement;
                  },
                }}
              />
            </Spin>
            <EntriesFooter align="center" gap={8}>
              <Text>Show</Text>
              <ShowEntriesSelect
                value={shownPageSize}
                onChange={(v) => setManualPageSize(Number(v))}
                options={pageSizeOptions(shownPageSize)}
              />
              <Text>entries</Text>
            </EntriesFooter>
          </DesktopTableWrap>
        )}
      </AppointmentsCard>

      <AppointmentDetailModal
        appointment={selectedAppointment}
        open={selectedAppointment !== null}
        onClose={() => setSelectedAppointment(null)}
      />
    </>
  );
}
